import {
  hasModerationChurnedContinue,
  useAccount,
  useContext,
  useIsAuthenticated,
} from "@mytour/platform";
import type {Context, FunctionalRole} from "@mytour/shared";
import React from "react";
import {Trans as Translate} from "react-i18next";
import {Navigate, useLocation} from "react-router";
import {ErrorBoundaryWrapper} from "./ErrorBoundaryRoute";

interface IPrivateRouteWrapperProps {
  children: React.ReactNode;
  requiredRoles?: FunctionalRole[];
}

export function PrivateRouteWrapper({children, requiredRoles = []}: IPrivateRouteWrapperProps) {
  const {isAuthenticated, isAuthenticatedLoading} = useIsAuthenticated();
  const {account, isSessionLoading} = useAccount();
  const location = useLocation();
  const {context, isContextLoading} = useContext();

  if (isSessionLoading || isAuthenticatedLoading || isContextLoading) {
    return <div />;
  }

  if (!isAuthenticated || !account) {
    return <Navigate to="/login" state={{from: location}} replace />;
  }

  const canBypassChurnedGate =
    context?.fromModeration && hasModerationChurnedContinue(context.companyId);
  if (context?.churned && !canBypassChurnedGate) {
    return <Navigate to="/compte-en-veille" replace />;
  }

  if (!isAuthorized(context, requiredRoles)) {
    return (
      <div className="insufficient-authority">
        <div className="alert alert-danger">
          <Translate i18nKey="error.http.403">
            You are not authorized to access this page.
          </Translate>
        </div>
      </div>
    );
  }

  return <ErrorBoundaryWrapper>{children}</ErrorBoundaryWrapper>;
}

function isAuthorized(context: Context | undefined, requiredRoles: FunctionalRole[]): boolean {
  if (context) {
    const role = context.role;
    if (requiredRoles.length === 0) {
      return true;
    }
    return requiredRoles.includes(role);
  }
  return false;
}
